// src/Hooks/useUsersManage.js

import { useState, useEffect } from 'react';
import { collection, onSnapshot, doc, writeBatch } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { signOut } from 'firebase/auth';
import { auth, db, functions } from '../Firebase/firebase';
import { UnsubscribeService } from '../Components/Unsubscribe/UnsubscribeService';

export function useUsersManage() {
  const [users, setUsers] = useState([]);
  const [initialViews, setInitialViews] = useState([]);
  const [newRows, setNewRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const [focusedRowId, setFocusedRowId] = useState(null);

  // Alert States
  const [showUnsaved, setShowUnsaved] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  // Last Admin Protection State
  const [showLastAdminAlert, setShowLastAdminAlert] = useState(false);
  const [lastAdminMessage, setLastAdminMessage] = useState('');

  // Unified Result Alert
  const [showResultAlert, setShowResultAlert] = useState(false);
  const [resultMessage, setResultMessage] = useState('');
  const [resultSeverity, setResultSeverity] = useState('success');

  // --- 1. FETCH DATA ---
  useEffect(() => {
    const unsubUsers = onSnapshot(
      collection(db, 'permittedUsers'),
      (snapshot) => {
        const fetched = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        fetched.sort((a, b) => (a.email || '').localeCompare(b.email || ''));
        setUsers(fetched);
        setLoading(false);
      },
      (error) => {
        // Silently ignore permission errors caused by intentional sign-outs
        if (error.code !== 'permission-denied') {
          console.error('Error fetching permittedUsers:', error);
        }
        setLoading(false);
      },
    );

    const unsubViews = onSnapshot(
      collection(db, 'initialViews'),
      (snapshot) => {
        const fetchedViews = snapshot.docs.map((doc) => ({
          id: doc.id,
          name: doc.data().name || 'Untitled',
          isArchived: doc.data().isArchived || false,
        }));
        fetchedViews.sort((a, b) => a.name.localeCompare(b.name));
        setInitialViews(fetchedViews);
      },
      (error) => {
        if (error.code !== 'permission-denied') {
          console.error('Error fetching initial views:', error);
        }
      },
    );

    return () => {
      unsubUsers();
      unsubViews();
    };
  }, []);

  // --- 2. ACTIONS ---

  const handleHeaderAction = (action) => {
    if (action === 'ADD_NEW') {
      const tempId = `temp_${Date.now()}`;
      const newRow = {
        id: tempId,
        email: '',
        initialViewId: '',
        canUpdateMarkers: false,
        canAccessPromo: false,
        isAdmin: false,
        isNew: true,
      };
      setNewRows((prev) => [...prev, newRow]);
      setFocusedRowId(tempId);
      setIsDirty(true);
    } else if (action === 'SAVE') {
      saveChanges();
    } else if (action === 'DELETE') {
      const allCurrentRows = [...users, ...newRows];
      const remainingRows = allCurrentRows.filter((row) => !selectedIds.includes(row.id));
      const remainingAdmins = remainingRows.filter((row) => row.isAdmin).length;

      const onlyDeletingNewRows = selectedIds.every((id) => id.startsWith('temp_'));

      if (remainingAdmins < 1 && !onlyDeletingNewRows) {
        setLastAdminMessage(
          'Unable to delete this user. You must have at least one Admin user.',
        );
        setShowLastAdminAlert(true);
      } else if (onlyDeletingNewRows) {
        setNewRows((prev) => prev.filter((row) => !selectedIds.includes(row.id)));
        setSelectedIds([]);
        if (newRows.length === selectedIds.length && !isDirty) {
          setIsDirty(false);
        }
      } else {
        setShowDelete(true);
      }
    } else if (action === 'CLOSE') {
      if (isDirty) setShowUnsaved(true);
    }
  };

  const handleFieldChange = (id, field, value) => {
    setIsDirty(true);

    const updateRow = (row) => {
      const updates = { [field]: value };
      if (field === 'isAdmin' && value === true) {
        updates.canUpdateMarkers = true;
        updates.canAccessPromo = true;
      }
      return { ...row, ...updates };
    };

    setNewRows((prev) => prev.map((row) => (row.id === id ? updateRow(row) : row)));
    setUsers((prev) => prev.map((row) => (row.id === id ? updateRow(row) : row)));
  };

  const handleCheckboxChange = (id) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((i) => i !== id);
      return [...prev, id];
    });
  };

  const handleConfirmDelete = async () => {
    const idsToDelete = [...selectedIds];

    // Prevent double execution and close UI instantly
    if (idsToDelete.length === 0) return;
    setShowDelete(false);
    setSelectedIds([]);

    const dbIds = idsToDelete.filter((id) => !id.startsWith('temp_'));
    const localIds = idsToDelete.filter((id) => id.startsWith('temp_'));

    if (dbIds.length > 0) {
      try {
        const usersToUnsubscribe = dbIds.map((id) => {
          const u = users.find((user) => user.id === id);
          return {
            id: u?.id || id,
            email: u?.email || id,
            userName: '',
          };
        });

        // 1. Run the UnsubscribeService FIRST while the user still has Admin Rules active!
        const result = await UnsubscribeService.execute(usersToUnsubscribe, {
          addToAudit: false, // Bypasses the Unsubscribe list entirely
          skipAuth: true,
        });

        // 2. NOW delete their permittedUsers document
        try {
          const batch = writeBatch(db);
          dbIds.forEach((id) => {
            const ref = doc(db, 'permittedUsers', id);
            batch.delete(ref);
          });
          await batch.commit();
        } catch (dbErr) {
          console.warn('Permitted Users delete skipped/failed:', dbErr.message);
        }

        // 3. Now that ALL Firestore data is cleanly wiped, delete the Firebase Auth accounts!
        const deleteUserByEmail = httpsCallable(functions, 'deleteUserByEmail');
        let selfDeleted = false;

        for (const email of dbIds) {
          try {
            await deleteUserByEmail({ email: email });
            // Check if the currently logged-in user just deleted themselves
            if (auth.currentUser && auth.currentUser.email === email) {
              selfDeleted = true;
            }
          } catch (e) {
            console.warn('Auth deletion skipped:', e);
          }
        }

        // If the admin deleted themselves, log them out gracefully
        if (selfDeleted) {
          await signOut(auth);
          return;
        }

        if (result && result.failCount > 0) {
          setResultMessage(
            'Accounts securely deleted, but some data cleanup was skipped due to permissions.',
          );
          setResultSeverity('warning');
          setShowResultAlert(true);
        } else {
          setResultMessage('Users successfully deleted.');
          setResultSeverity('success');
          setShowResultAlert(true);
        }
      } catch (error) {
        console.error('Error fully deleting users:', error);
        setResultMessage('Failed to fully delete users. Check console.');
        setResultSeverity('error');
        setShowResultAlert(true);
      }
    }

    if (localIds.length > 0) {
      setNewRows((prev) => prev.filter((row) => !localIds.includes(row.id)));
    }
  };

  const saveChanges = async () => {
    const allRows = [...users, ...newRows];
    const totalAdmins = allRows.filter((row) => row.isAdmin).length;

    if (totalAdmins < 1) {
      setLastAdminMessage(
        'Unable to Save this change. You must have at least one Admin user.',
      );
      setShowLastAdminAlert(true);
      return;
    }

    const batch = writeBatch(db);
    const validNewRows = newRows.filter((r) => r.email && r.email.trim() !== '');

    validNewRows.forEach((row) => {
      const emailKey = row.email.toLowerCase().trim();
      const docRef = doc(db, 'permittedUsers', emailKey);

      const isAdmin = row.isAdmin || false;
      const canUpdateMarkers = isAdmin ? true : row.canUpdateMarkers || false;
      const canAccessPromo = isAdmin ? true : row.canAccessPromo || false;

      batch.set(docRef, {
        email: row.email,
        initialViewId: row.initialViewId || null,
        canUpdateMarkers: canUpdateMarkers,
        canAccessPromo: canAccessPromo,
        isAdmin: isAdmin,
        createdAt: new Date().toISOString(),
      });
    });

    users.forEach((row) => {
      const docRef = doc(db, 'permittedUsers', row.id);

      const isAdmin = row.isAdmin || false;
      const canUpdateMarkers = isAdmin ? true : row.canUpdateMarkers || false;
      const canAccessPromo = isAdmin ? true : row.canAccessPromo || false;

      batch.update(docRef, {
        email: row.email,
        initialViewId: row.initialViewId || null,
        canUpdateMarkers: canUpdateMarkers,
        canAccessPromo: canAccessPromo,
        isAdmin: isAdmin,
      });
    });

    // Clear local state BEFORE committing to prevent duplicate rows from appearing
    // due to Firestore's local latency compensation onSnapshot firing
    setNewRows([]);
    setIsDirty(false);

    try {
      await batch.commit();
      setResultMessage('Permitted Users saved successfully.');
      setResultSeverity('success');
      setShowResultAlert(true);
    } catch (error) {
      console.error('Error saving permitted users:', error);
      setResultMessage('Failed to save users. Check console.');
      setResultSeverity('error');
      setShowResultAlert(true);
    }
  };

  const handleCloseConfirmed = () => {
    setNewRows([]);
    setIsDirty(false);
    setShowUnsaved(false);
  };

  const displayRows = [...users, ...newRows];

  return {
    state: {
      displayRows,
      newRows,
      selectedIds,
      loading,
      initialViews,
      focusedRowId,
    },
    flags: {
      isDirty,
      canSave: isDirty,
      canDelete: selectedIds.length > 0,
      canClose: isDirty,
    },
    alerts: {
      showUnsaved,
      setShowUnsaved,
      showDelete,
      setShowDelete,
      showLastAdminAlert,
      setShowLastAdminAlert,
      lastAdminMessage,
      showResultAlert,
      setShowResultAlert,
      resultMessage,
      resultSeverity,
    },
    actions: {
      handleHeaderAction,
      handleFieldChange,
      handleCheckboxChange,
      handleConfirmDelete,
      handleCloseConfirmed,
      setFocusedRowId,
    },
  };
}
